import React, { useContext } from 'react';
import { useTranslation, Router } from '@com/core/common/translations/i18n';
import { breakpoints } from '@com/core/common/styles/baseStyles';
import styled from 'styled-components';
import ContentLoader from 'react-content-loader';
import Dropdown from '@com/core/common/components/Dropdown';
import { OptionTypeBase } from 'react-select';
// import SideNavDesktop from '@com/core/common/components/account/sideNav/SideNavDesktop';
import { TSideNavItem } from '@com/core/common/types/navigation/navigation';
import { CDN_IMG_BASE } from '@com/core/common/baseConfig';
import ErrorBox from './ErrorBox';
import BlockButton from './BlockButton';
// import { saveLocaleAsCookie } from '../../utils/client';

type TProps = {
    backRoute?: { href: string; pathname?: string; query?: { [key: string]: string } }; // BACK - route to go to eg: /orders
    backLabel?: string; // BACK - label to show
    children: JSX.Element; // whichever content to render
    titleKey?: string; // title - if any
    section: TSideNavItem['code']; // section to highlight on nav
    subtitleKey?: string; // subtitle - if any
    isLoading: boolean; // isLoading animation, depends on section
    error: string | null; // error - if any
    ctaTitle?: string; // CTA button (right) title
    ctaAction?: () => void; // CTA button (right) action
    filterTitle?: string;
    filterSelectedOption?: OptionTypeBase | null;
    filterOptions?: OptionTypeBase[];
    filterAction?: (selectedOption: OptionTypeBase) => void;
    filterPlaceholder?: string;
};

const getLoadingPlaceholder = (section: TSideNavItem['code']): JSX.Element => {
    switch (section) {
        case 'profile':
            return (
                <ContentLoader
                    uniqueKey="profile"
                    viewBox="0 0 400 180"
                    backgroundColor="#f7f7fa"
                    foregroundColor="#f1f1f1">
                    <rect x="0" y="-1" rx="3" ry="3" width="400" height="50" />
                    <rect x="0" y="60" rx="3" ry="3" width="400" height="50" />
                    <rect x="0" y="120" rx="3" ry="3" width="400" height="50" />
                </ContentLoader>
            );
        case 'orders':
            return (
                <ContentLoader
                    uniqueKey="orders"
                    viewBox="0 0 400 500"
                    backgroundColor="#f7f7fa"
                    foregroundColor="#f1f1f1">
                    <rect x="0" y="0" rx="3" ry="3" width="400" height="150" />
                    <rect x="0" y="160" rx="3" ry="3" width="400" height="50" />
                    <rect x="0" y="220" rx="3" ry="3" width="400" height="50" />
                    <rect x="0" y="280" rx="3" ry="3" width="400" height="50" />
                    <rect x="0" y="340" rx="3" ry="3" width="400" height="50" />
                </ContentLoader>
            );
        default:
            return (
                <ContentLoader
                    uniqueKey="default"
                    viewBox="0 0 400 180"
                    backgroundColor="#f7f7fa"
                    foregroundColor="#f1f1f1">
                    <rect x="0" y="-1" rx="3" ry="3" width="400" height="50" />
                    <rect x="0" y="60" rx="3" ry="3" width="400" height="50" />
                    <rect x="0" y="120" rx="3" ry="3" width="400" height="50" />
                </ContentLoader>
            );
    }
};

const Layout = ({
    backLabel,
    backRoute,
    children,
    ctaAction,
    ctaTitle,
    error,
    filterAction,
    filterOptions,
    filterPlaceholder,
    filterSelectedOption,
    filterTitle,
    isLoading,
    section,
    subtitleKey,
    titleKey,
}: TProps): JSX.Element => {
    const { t } = useTranslation('common');

    // useEffect(() => {
    //     saveLocaleAsCookie(locale);
    // }, [locale]);

    const onBack = (): void => {
        if (backRoute) {
            const { href, pathname, query } = backRoute;

            if (pathname) {
                Router.push({ pathname, query }, href);
            } else {
                Router.push(href);
            }
        }
    };

    const showHeader = !!titleKey;
    const showActionContainer = (!!filterTitle || !!ctaTitle) && !isLoading;

    let content = children;

    if (error) {
        content = (
            <ErrorBox
                titleKey="something-went-wrong"
                subtitleKey="please-try-again"
                ctaClick={(): void => {
                    window.location.reload();
                }}
                ctaTextKey="retry"
            />
        );
    }

    return (
        <>
            <LayoutContainer className="siteWidthContainer">
                {/* <SideNavDesktopContainer>
                    <SideNavDesktop section={section} />
                </SideNavDesktopContainer> */}
                <ChildrenContainer>
                    {!!backRoute && !!backLabel && (
                        <BackLink onClick={onBack}>
                            <img src={`${CDN_IMG_BASE}/simpleIcons/arrow-left.svg`} alt="Back" />
                            <span>{t(backLabel)}</span>
                        </BackLink>
                    )}

                    {(showHeader || showActionContainer) && (
                        <PageHeaderContainer className={!!titleKey || !!subtitleKey ? 'space' : undefined}>
                            {showHeader && (
                                <div className="titleContainer">
                                    {titleKey && <h1 data-qa={`lbl_title_${titleKey}`}>{t(titleKey)}</h1>}
                                    {subtitleKey && <p data-qa={`lbl_subtTitle_${titleKey}`}>{t(subtitleKey)}</p>}
                                </div>
                            )}
                            {showActionContainer && (
                                <ActionContainer>
                                    {filterTitle && filterAction && filterOptions && filterOptions.length && (
                                        <FilterContainer>
                                            <FilterTitle data-qa={`lbl_filterTitle_${filterTitle}`}>
                                                {t(filterTitle)}
                                            </FilterTitle>
                                            <DropdownContainer>
                                                <Dropdown
                                                    placeholder={filterPlaceholder}
                                                    onChange={filterAction}
                                                    options={filterOptions}
                                                    selectedOption={filterSelectedOption}
                                                />
                                            </DropdownContainer>
                                        </FilterContainer>
                                    )}
                                    {ctaTitle && ctaAction && (
                                        <ButtonContainer>
                                            <BlockButton onClick={ctaAction}>{ctaTitle}</BlockButton>
                                        </ButtonContainer>
                                    )}
                                </ActionContainer>
                            )}
                        </PageHeaderContainer>
                    )}

                    {isLoading ? <LoaderContainer>{getLoadingPlaceholder(section)}</LoaderContainer> : content}
                </ChildrenContainer>
            </LayoutContainer>
        </>
    );
};

const LayoutContainer = styled.div`
    padding-top: 24px;
    padding-bottom: 64px;
    display: flex;
`;

const BackLink = styled.button`
    display: flex;
    align-items: center;
    margin-bottom: 24px;

    &:hover {
        opacity: 0.7;
    }

    img {
        width: 18px;
        height: 18px;
        ${({ theme: { right } }): string => `margin-${right}: 8px`};
        ${({ theme }): string => (theme.isRTL ? `transform: scale(-1);` : '')}
    }

    span {
        font-size: 0.9375rem;
    }
`;

// const SideNavDesktopContainer = styled.div`
//     @media ${breakpoints.minTablet} {
//         display: none;
//         flex: 0 0 200px;
//         max-width: 200px;
//     }

//     @media ${breakpoints.minDesktop} {
//         flex: 0 0 230px;
//         max-width: 230px;
//     }
// `;

// ------ Page Header
const PageHeaderContainer = styled.div`
    display: flex; /* titleContainer < ActionContainer */
    align-items: center;
    margin-bottom: 24px;

    .titleContainer {
        flex: 1;

        h1 {
            font-size: 1.5rem;
            font-weight: bold;
        }

        > p {
            margin-top: 4px;
            font-size: 0.9375rem;
            color: ${({ theme }): string => theme.colors.bodyLight};
        }
    }
`;

// ------ Actions
const ActionContainer = styled.div`
    display: flex;
    justify-content: flex-end;
`;
const FilterContainer = styled.div`
    display: flex;
    flex: 0 1 50%;
    align-items: center;
    justify-content: flex-end;
    flex: 1;
    ${({ theme: { right, spacing } }): string => `padding-${right}: ${spacing.sm}`};

    @media ${breakpoints.minMobile} {
        flex: 0 1 50%;
    }

    @media ${breakpoints.minTablet} {
        ${({ theme: { left, spacing } }): string => `padding-${left}: ${spacing.md}`};
        ${({ theme: { right } }): string => `padding-${right}: 0`};

        flex: 1;
        justify-content: flex-end;
    }
    user-select: none;
`;

const FilterTitle = styled.span`
    color: ${({ theme }): string => theme.colors.body};
    ${({ theme: { right } }): string => `padding-${right}: 10px`};
    white-space: nowrap;
`;
const DropdownContainer = styled.div`
    width: 50%;
    min-width: 140px;
`;

const ButtonContainer = styled.div`
    display: flex;
    align-items: center;
    ${({ theme: { right } }): string => `margin-${right}: 10px`};

    @media ${breakpoints.maxMobile} {
        justify-content: center;
        padding: 0;
    }
`;

// ------ Children
const ChildrenContainer = styled.div`
    flex: 1;
    min-width: 0;
`;

const LoaderContainer = styled.div`
    padding: 0;
    @media ${breakpoints.minDesktop} {
        padding: 0;
    }
`;

export default Layout;
